import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, UserRound, Radar, BellRing, Compass, DoorOpen, ShieldCheck,
} from "lucide-react";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";

const PILLARS = [
  { icon: Radar, title: "Monitors your journey", body: "Your RM watches your Capital Readiness Score, your document pack and your open action items — so they always know where you stand." },
  { icon: BellRing, title: "Checks in proactively", body: "No chasing. Your RM reaches out when a step stalls, a document is missing, or your score moves — with a clear next action." },
  { icon: Compass, title: "Connects the right resources", body: "Analyst support, masterclasses, templates or a specialist introduction — your Relationship Manager brings the right help at the right moment." },
  { icon: DoorOpen, title: "Opens the door to funders", body: "Once your Capital Readiness Score is Market Ready (70+), your Relationship Manager hand-picks mandate-fit funders from our network and makes the introduction." },
];

const TIMELINE = [
  ["Get my report", "Your Relationship Manager is assigned the moment your Capital Readiness Report is unlocked."],
  ["First check-in", "Within two working days your Relationship Manager introduces themselves and reviews your snapshot and documents."],
  ["Ongoing support", "Regular check-ins as you upload documents, run your analysis and work through your roadmap."],
  ["Funder introductions", "At Market Ready, your Relationship Manager opens conversations with funders whose mandate fits your business."],
];

export default function YourRM() {
  return (
    <div data-testid="your-rm-page" className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />

      <header className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
          <Link to="/for-business" data-testid="your-rm-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to Clinq for Business
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-[#7BD3CE]">We Are With You Every Step</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="your-rm-heading">
            Your Personal <span className="text-emerald-300">Relationship Manager.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/75 sm:text-lg">
            Every founder on Clinq is assigned a dedicated Relationship Manager — a real person who knows your
            business, tracks your progress, and walks with you every step of the way to funding.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/get-started" data-testid="your-rm-cta-top"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#066667]">
              Get started — get my Capital Readiness Report <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/sample-report" data-testid="your-rm-sample-link"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 px-6 py-3 text-sm font-semibold text-white/85 transition-colors hover:border-white hover:text-white">
              See a sample report
            </Link>
          </div>
        </div>
      </header>

      <section className="border-b border-neutral-200">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D1A330]">What your Relationship Manager does</p>
          <h2 className="mt-3 max-w-2xl text-base font-extrabold text-neutral-900 md:text-lg">
            One person, accountable for your progress — from first upload to funder introduction.
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="your-rm-pillars">
            {PILLARS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="rounded-2xl border border-neutral-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-[#0A8B85]/40 hover:shadow-lg">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0A8B85]/10 text-[#0A8B85]"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 text-base font-bold text-neutral-900">{c.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F4F8F8]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-16 lg:grid-cols-[1fr_1.1fr] lg:py-20">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">How it works</p>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">From unlock to introduction.</h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">
              Your Relationship Manager is assigned automatically when you get your Capital Readiness Report.
              There is nothing extra to book or pay for — it is part of the journey.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-xl border border-[#0A8B85]/25 bg-white px-4 py-3 text-sm text-neutral-700">
              <ShieldCheck className="h-4 w-4 text-[#0A8B85]" /> Every RM is Clinq-certified through our Relationship Manager training programme.
            </div>
          </div>
          <ol className="space-y-4" data-testid="your-rm-timeline">
            {TIMELINE.map(([t, b], i) => (
              <li key={t} className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0A8B85] text-xs font-extrabold text-white">0{i + 1}</span>
                <div>
                  <p className="text-sm font-bold text-neutral-900">{t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600">{b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#04302E] text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-6 py-16 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10"><UserRound className="h-6 w-6 text-emerald-300" /></span>
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Ready to meet your Relationship Manager?</h2>
              <p className="mt-2 max-w-xl text-base text-white/75">Take the free 5-minute Get Started quiz, get your Capital Readiness Report, and your Relationship Manager will be in touch.</p>
            </div>
          </div>
          <Link to="/get-started" data-testid="your-rm-cta-bottom"
            className="inline-flex items-center gap-2 rounded-md bg-[#0A8B85] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#066667]">
            Get started free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
